import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { usePeriod } from "@/context/PeriodContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, SendHorizonal, Loader2, ArrowUpRight, AlertOctagon, MessageSquarePlus, TrendingUp, HelpCircle, BookOpen } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar } from "recharts";

/**
 * Sprint 25 — AI Financial Copilot chat panel. Used by both the /copilot
 * page and the floating FAB drawer. Every answer is grounded in the tenant's
 * actual context (server-side `build_context`), so hallucinations are not
 * possible unless the LLM misbehaves — in which case the "missing" list
 * on the response surfaces the gap.
 */
export default function CopilotPanel({ compact = false }) {
  const { lang } = useI18n();
  const { periodKey } = usePeriod();
  const [sessionId, setSessionId] = useState(null);
  const [msgs, setMsgs] = useState([]);                     // {role,content|answer}
  const [suggestions, setSuggestions] = useState([]);
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const t = (tr, de) => (lang === "de" ? de : tr);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/copilot/suggestions?lang=${lang}`);
        setSuggestions(data.items || []);
      } catch { setSuggestions([]); }
    })();
  }, [lang]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" });
  }, [msgs, sending]);

  const send = async (text) => {
    const question = (text || q).trim();
    if (!question || sending) return;
    setMsgs((m) => [...m, { role: "user", content: question }]);
    setQ("");
    setSending(true);
    try {
      const { data } = await api.post("/copilot/ask", {
        question, period_key: periodKey, session_id: sessionId, lang,
      });
      setSessionId(data.session_id);
      setMsgs((m) => [...m, {
        role: "assistant",
        answer: data.answer,
        warnings: data.warnings || [],
        ratios: data.ratios || {},
        missing: data.missing || [],
      }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || t("Cevap alınamadı", "Antwort fehlgeschlagen"));
      setMsgs((m) => [...m, { role: "error", content: msg }]);
    } finally { setSending(false); }
  };

  const newChat = () => { setSessionId(null); setMsgs([]); };

  return (
    <div className={`flex flex-col h-full ${compact ? "" : "min-h-[70vh]"}`} data-testid="copilot-panel">
      {/* Header */}
      <div className="flex items-center gap-2 px-2 pt-2 pb-3 border-b">
        <Sparkles className="w-4 h-4 text-primary" />
        <div className="text-sm font-semibold flex-1">
          {t("AI Finansal Danışman", "AI-Finanzberater")}
          {periodKey && <span className="ml-2 text-xs text-muted-foreground font-mono">{periodKey}</span>}
        </div>
        {msgs.length > 0 && (
          <Button size="sm" variant="outline" className="h-7 gap-1" onClick={newChat} data-testid="copilot-new">
            <MessageSquarePlus className="w-3.5 h-3.5" /> {t("Yeni sohbet", "Neuer Chat")}
          </Button>
        )}
      </div>

      {/* Message list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 p-2" data-testid="copilot-messages">
        {msgs.length === 0 && (
          <div className="text-center text-sm text-muted-foreground py-6">
            <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <div>{t("Sadece senin restoranın verileriyle konuşurum.", "Ich antworte nur mit den Daten deines Restaurants.")}</div>
            <div className="text-xs mt-1">{t("Aşağıdan bir öneriyle başla.", "Starte mit einem Vorschlag.")}</div>
          </div>
        )}
        {msgs.map((m, i) => (
          <MessageBubble key={i} m={m} lang={lang} onFollowUp={send} />
        ))}
        {sending && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="copilot-typing">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("AI verilerini inceliyor…", "KI analysiert deine Daten…")}
          </div>
        )}
      </div>

      {/* Suggestions */}
      {msgs.length === 0 && suggestions.length > 0 && (
        <div className="border-t p-2 space-y-1" data-testid="copilot-suggestions">
          {suggestions.map((s, i) => (
            <button
              key={i}
              onClick={() => send(s)}
              className="w-full text-left text-sm rounded-lg border px-3 py-2 hover:bg-muted/60 flex items-center justify-between gap-2"
              data-testid={`copilot-suggestion-${i}`}
            >
              <span className="truncate">{s}</span>
              <ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      <div className="border-t p-2 flex items-end gap-2">
        <Textarea
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={t("Bir soru yaz…", "Frag etwas…")}
          className="min-h-[42px] max-h-32 resize-none"
          data-testid="copilot-input"
        />
        <Button onClick={() => send()} disabled={sending || !q.trim()} data-testid="copilot-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizonal className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

/* ------- Message rendering ------- */

const WARNING_META = {
  negative_cash:            { tr: "Nakit akışı negatif",     de: "Negativer Cashflow" },
  unusual_expense_increase: { tr: "Beklenmedik gider artışı", de: "Ungewöhnlicher Anstieg" },
  overdue_ap:               { tr: "Geciken tedarikçi ödemesi", de: "Überfällige Verbindlichk." },
  low_inventory:            { tr: "Düşük stok",              de: "Niedriger Bestand" },
  high_food_cost:           { tr: "Gıda maliyeti yüksek",    de: "Wareneinsatz zu hoch" },
  high_labor_cost:          { tr: "Personel maliyeti yüksek", de: "Personalkosten zu hoch" },
  missing_daily_close:      { tr: "Eksik günlük kapanış",    de: "Fehlender Tagesabschluss" },
};

function MessageBubble({ m, lang, onFollowUp }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-primary text-primary-foreground px-3 py-2 text-sm" data-testid="copilot-msg-user">
          {m.content}
        </div>
      </div>
    );
  }
  if (m.role === "error") {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/5 text-destructive text-sm px-3 py-2">
        {m.content}
      </div>
    );
  }
  const a = m.answer || {};
  const chart = a.chart;
  return (
    <div className="flex justify-start">
      <Card className="max-w-[92%] shadow-elev-1" data-testid="copilot-msg-assistant">
        <CardContent className="p-3 space-y-2.5">
          <div className="text-sm font-medium leading-snug">{a.summary || "—"}</div>

          {(m.warnings || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {m.warnings.map((w) => (
                <Badge key={w} variant="outline" className="border-amber-300 text-amber-700 gap-1">
                  <AlertOctagon className="w-3 h-3" />
                  {(WARNING_META[w] || {})[lang === "de" ? "de" : "tr"] || w}
                </Badge>
              ))}
            </div>
          )}

          {(a.evidence || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                {lang === "de" ? "Belege" : "Kanıtlar"}
              </div>
              <ul className="text-xs list-disc pl-4 space-y-0.5">
                {a.evidence.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>
          )}

          {(a.recommendations || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                {lang === "de" ? "Empfehlung" : "Öneriler"}
              </div>
              <ul className="text-xs list-disc pl-4 space-y-0.5">
                {a.recommendations.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          )}

          {chart && Array.isArray(chart.series) && chart.series.length > 1 && (
            <div className="h-32 -mx-1" data-testid="copilot-chart">
              <ResponsiveContainer width="100%" height="100%">
                {chart.kind === "bar" ? (
                  <BarChart data={chart.series}>
                    <XAxis dataKey="x" fontSize={10} />
                    <YAxis fontSize={10} />
                    <Tooltip />
                    <Bar dataKey="y" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                ) : (
                  <LineChart data={chart.series}>
                    <XAxis dataKey="x" fontSize={10} />
                    <YAxis fontSize={10} />
                    <Tooltip />
                    <Line type="monotone" dataKey="y" stroke="#6366f1" strokeWidth={2} dot={false} />
                  </LineChart>
                )}
              </ResponsiveContainer>
              {chart.label && <div className="text-[10px] text-center text-muted-foreground -mt-2">{chart.label}</div>}
            </div>
          )}

          {(m.missing || []).length > 0 && (
            <div className="text-[11px] text-amber-700 flex items-start gap-1">
              <HelpCircle className="w-3 h-3 mt-0.5" />
              <span>{lang === "de" ? "Fehlende Daten:" : "Eksik veri:"} {m.missing.join(", ")}</span>
            </div>
          )}

          {(a.follow_ups || []).length > 0 && (
            <div className="pt-1 flex flex-wrap gap-1" data-testid="copilot-followups">
              {a.follow_ups.slice(0, 4).map((f, i) => (
                <Button key={i} size="sm" variant="outline" className="h-7 text-xs" onClick={() => onFollowUp(f)} data-testid={`copilot-fu-${i}`}>
                  <TrendingUp className="w-3 h-3 mr-1" /> {f}
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
